/**
 * Tiny helpers for safely turning an unknown thrown value (the type of
 * `catch (err)` and React Query's `onError(err: unknown)` callback)
 * into a human-readable string.
 *
 * Why a dedicated module instead of repeating `err instanceof Error`?
 *   • The browser, Supabase JS, and our own validation layer all throw
 *     slightly different shapes. Centralising the narrowing keeps the
 *     happy path one line at the call site:
 *
 *       onError: (err) => toast.error(getErrorMessage(err)),
 *
 *   • Lets us tighten the surface in exactly one place if we ever want
 *     to e.g. attach Sentry tags or scrub PII before display.
 *   • Avoids the `(err: any) => err.message` anti-pattern that ESLint
 *     flags as `@typescript-eslint/no-explicit-any` everywhere.
 */

/**
 * Best-effort extraction of a display string from an unknown thrown
 * value. Always returns a non-empty string so callers can use it
 * directly in `toast.error(...)` without further checks.
 */
export function getErrorMessage(err: unknown, fallback = 'Unbekannter Fehler'): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err.length > 0) return err;
  if (err && typeof err === 'object') {
    // Supabase errors carry { message, details, hint } and our own
    // wrappers carry { message }. Either path is fine.
    const candidate = (err as { message?: unknown }).message;
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return fallback;
}
